import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Layers, RefreshCw } from "lucide-react";
import { providerService } from "@/services/providerService";
import { formatPrice } from "@/lib/format";

const coverageLabel = (item) => {
  if (item.engine_skip) return <span className="text-[#EF4444]">fora do engine</span>;
  if (item.visible) return <span className="text-[#10B981]">visível</span>;
  return <span className="text-[#F59E0B]">oculto</span>;
};


export const ProviderCoverageCard = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await providerService.coverage());
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const providers = data?.providers || [];

  return (
    <section data-testid="provider-coverage-card"
             className="rounded-md border border-[#22252F] bg-[#12141A] p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Layers className="h-4 w-4 text-[#60A5FA]" />
          <h2 className="font-heading text-lg font-light tracking-tight text-white md:text-lg">
            Cobertura por provedor
          </h2>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
            {data?.assets_total ?? 0} ativos no catálogo
          </span>
        </div>
        <button onClick={load} disabled={loading} data-testid="coverage-reload"
                className="inline-flex items-center gap-2 rounded-md border border-[#22252F] px-3 py-2 text-xs text-[#94A3B8] transition-colors hover:border-[#2563EB]/50 hover:text-white disabled:opacity-40">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </button>
      </div>

      <div className="mt-5 space-y-2">
        {providers.map((p) => {
          const expanded = open === p.slug;
          return (
            <div key={p.slug} data-testid={`coverage-provider-${p.slug}`}
                 className="rounded-md border border-[#1A1D25] bg-[#0F1116]">
              <button onClick={() => setOpen(expanded ? "" : p.slug)}
                      data-testid={`coverage-toggle-${p.slug}`}
                      className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left">
                {expanded ? <ChevronDown className="h-4 w-4 text-[#64748B]" />
                          : <ChevronRight className="h-4 w-4 text-[#64748B]" />}
                <span className="text-sm text-white">{p.name}</span>
                <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${p.enabled ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {p.enabled ? "ativo" : "inativo"}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                  prioridade {p.priority} · {p.call_interval_seconds}s
                </span>
                <span className="ml-auto font-mono text-xs text-[#94A3B8]">
                  a servir <span className="text-[#10B981]">{p.serving_count}</span> / suporta {p.supported_count}
                </span>
              </button>

              {expanded ? (
                <div className="overflow-x-auto border-t border-[#1A1D25] px-4 py-3">
                  {p.items.length === 0 ? (
                    <p className="py-3 text-sm text-[#64748B]">Nenhum ativo do catálogo é suportado por este provedor.</p>
                  ) : (
                    <table className="w-full min-w-[620px] text-left text-sm">
                      <thead className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                        <tr>
                          <th className="py-2 pr-6">Ativo</th>
                          <th className="py-2 pr-6">Mercado</th>
                          <th className="py-2 pr-6 text-right">Preço</th>
                          <th className="py-2 pr-6">Fonte atual</th>
                          <th className="py-2">Estado</th>
                        </tr>
                      </thead>
                      <tbody data-testid={`coverage-body-${p.slug}`}>
                        {p.items.map((i) => (
                          <tr key={`${p.slug}-${i.symbol}`} className="border-t border-[#1A1D25]">
                            <td className="py-2 pr-6 font-mono text-white">{i.symbol}</td>
                            <td className="py-2 pr-6 font-mono text-xs text-[#64748B]">{i.market}</td>
                            <td className="py-2 pr-6 text-right font-mono text-xs text-[#94A3B8]">
                              {i.last_price ? formatPrice(i.last_price) : "—"}
                            </td>
                            <td className={`py-2 pr-6 font-mono text-[10px] uppercase ${i.serving ? "text-[#10B981]" : "text-[#64748B]"}`}>
                              {i.serving ? "este provedor" : "outro provedor"}
                            </td>
                            <td className="py-2 font-mono text-[10px] uppercase">
                              {coverageLabel(i)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {data?.uncovered?.length ? (
        <div data-testid="coverage-uncovered" className="mt-5 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#F59E0B]">
            Sem provedor configurado ({data.uncovered.length})
          </p>
          <p className="mt-2 font-mono text-xs text-[#94A3B8]">
            {data.uncovered.map((u) => `${u.symbol} (${u.market})`).join(" · ")}
          </p>
        </div>
      ) : null}
    </section>
  );
};
